/**
 * WebMCP progressive enhancement.
 *
 * Registers a read-only `list_pages` tool with the browser's Web Model Context
 * API so in-browser AI agents can discover the main pages of the ComplyHub
 * marketing website. If the API is unavailable, this module does nothing.
 */

interface WebMcpToolResult {
  content: Array<{ type: "text"; text: string }>;
}

interface WebMcpToolDescriptor {
  name: string;
  description: string;
  inputSchema: Record<string, unknown>;
  execute: (args: Record<string, unknown>) => Promise<WebMcpToolResult> | WebMcpToolResult;
}

interface WebMcpModelContext {
  provideContext: (context: { tools: WebMcpToolDescriptor[] }) => void;
}

declare global {
  interface Navigator {
    modelContext?: WebMcpModelContext;
  }
}

const PAGES: Array<{ path: string; title: string }> = [
  { path: "/", title: "Home" },
  { path: "/product", title: "Product" },
  { path: "/features", title: "Features" },
  { path: "/pricing", title: "Pricing" },
  { path: "/demo", title: "Book a Demo" },
  { path: "/blog", title: "Blog" },
  { path: "/resources", title: "Resources" },
  { path: "/podcast", title: "The Governance Shift Podcast" },
  { path: "/compliance-scorecard/start", title: "Compliance Scorecard" },
  { path: "/governance-scorecard", title: "Governance Scorecard" },
  { path: "/book", title: "The 8 Critical Drivers (Book)" },
  { path: "/faq", title: "FAQ" },
  { path: "/compliance", title: "How ComplyHub maps to the 2025 Standards" },
  { path: "/contact", title: "Contact" },
];

let registered = false;

export function registerWebMcpTools(): void {
  if (registered) return;

  try {
    if (
      typeof navigator === "undefined" ||
      !navigator.modelContext ||
      typeof navigator.modelContext.provideContext !== "function"
    ) {
      return;
    }

    navigator.modelContext.provideContext({
      tools: [
        {
          name: "list_pages",
          description:
            "List the main pages available on the ComplyHub marketing website, with their paths and titles. Optionally filter by a case-insensitive substring.",
          inputSchema: {
            type: "object",
            properties: {
              filter: {
                type: "string",
                description:
                  "Optional case-insensitive substring to filter page paths or titles.",
              },
            },
            additionalProperties: false,
          },
          execute: (args) => {
            const raw = args?.filter;
            const query = typeof raw === "string" ? raw.trim().toLowerCase() : "";
            const matches = query
              ? PAGES.filter(
                  (page) =>
                    page.path.toLowerCase().includes(query) ||
                    page.title.toLowerCase().includes(query),
                )
              : PAGES;

            return {
              content: [
                { type: "text", text: JSON.stringify({ pages: matches }, null, 2) },
              ],
            };
          },
        },
      ],
    });

    registered = true;
  } catch {
    // WebMCP is a progressive enhancement — never surface errors to the page.
  }
}